<template>
  <header
      ref="root"
      :style="`top:${header.top}px`"
      class="d-print-none"
      :class="{
        'slide-up': hideHeader,
        'desktop': isDesktop,
        'mobil': isMobile,
      }"
  >
    <HeaderTop
      v-if="menuItemsTop.length"
      :items="menuItemsTop"
    />
    <nav class="navbar navbar-expand">
      <div class="container-fluid">
        <div
            ref="logoBar"
            class="row brand-row g-0 d-desktop-block"
            :class="{ 'justify-content-end': isDesktop }"
        >
          <div
              v-if="partner && partnerActive"
              class="col-auto d-desktop-inline-block align-self-center"
          >
            <IoRouterLink
                v-if="partner.url"
                :link-url="partner.url"
                link-target="_blank"
                class="brand-image-link partner"
            >
              <span
                  v-html="partner.thumbnail"
                  :title="partner.title"
              ></span>
            </IoRouterLink>
            <span
                v-else
                v-html="partner.thumbnail"
                :title="partner.title"
            ></span>
          </div>
          <div class="col-auto d-desktop-inline-block">
            <IoRouterLink
                link-url="/"
                class="brand-image-link"
            >
              <img
                  :src="logoSrc"
                  alt=""
                  loading="lazy"
              >
            </IoRouterLink>
          </div>
        </div>
        <div
            ref="navBar"
            class="d-flex navbar-nav align-items-center text-center col"
        >
          <div class="col">
            <HeaderMain :items="menuItemsMain" />
          </div>
          <a
              class="nav-link nav-link__other"
              :href="customerPortalUrl"
              title="Zum Kundencenter"
              target="_blank"
          >
            <FontAwesomeIcon icon="fa-light fa-circle-user" />
            <span class="sr-only">
                Kundencenter
            </span>
          </a>
          <a
              class="nav-link nav-link__other"
              :class="{ 'last': isDesktop && !cartEnabled }"
              title="Suche öffnen"
              href="/suche/"
              @click.prevent="toggleSearch"
          >
            <FontAwesomeIcon icon="fa-light fa-search" />
            <span class="sr-only">
              Suche
            </span>
          </a>
          <a
              v-if="cartEnabled"
              class="nav-link nav-link__other"
              :class="{ 'last': isDesktop && cartEnabled }"
              href="/warenkorb/"
              title="Warenkorb öffnen"
              @click.prevent="toggleCart"
          >
            <IconCart/>
            <span class="sr-only">
              Warenkorb
            </span>
          </a>
        </div>
      </div>
    </nav>
    <PageBanner/>
  </header>
  <BaseFixedAside v-if="!isMobile"/>
  <IoSearchForm/>
  <IoSidebar :items="menuItems"/>
  <CartWidget/>
  <MobileNavigation v-if="isMobile"/>
  <ScrollTopButton v-if="isMobile"/>
</template>

<script lang="ts" setup>
import {
  reactive,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { env } from '@/helpers';
import PartnerInterface from '@/interfaces/PartnerInterface';
import IoSearchForm from '@/components/IoSearchForm.vue';
import MenuInterface from '@/interfaces/MenuInterface';
import HeaderMain from '@/components/BaseHeader/BaseHeaderMain.vue';
import BaseFixedAside from '@/components/aside/BaseFixedAside.vue';
import CartWidget from '@/components/CartWidget.vue';
import HeaderTop from '@/components/BaseHeader/BaseHeaderTop.vue';
import IconCart from '@/components/IconCart.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleUser,
  faSearch,
  faBars,
} from '@fortawesome/pro-light-svg-icons';
import ScrollTopButton from '@/components/ScrollTopButton.vue';
import PageBanner from '@/components/PageBanner.vue';
import MobileNavigation from '@/components/MobileNavigation.vue';
import IoSidebar from '@/components/IoSidebar.vue';

library.add(faCircleUser, faSearch, faBars);
const store = useStore();
const logoSrc = window.ioappConfig.logo;
const partner = computed<PartnerInterface>(() => store.getters['partner/current']);
const partnerActive = computed(() => store.getters['partner/active']);
const logoBar = ref();
const navBar = ref();
const isMobile = ref(false);
const isDesktop = computed(() => !isMobile.value);
const { customerPortalUrl } = window.ioappConfig;

const header = reactive({
  top: 0,
  $el: null as null | HTMLElement,
});

const headerHeight = ref<number>(0);
const wpAdminBarHeight = ref<number>(0);
const setHeaderPos = () => {
  header.top = 0;

  nextTick(() => {
    const $wpAdminBar = document.querySelector('#wpadminbar') as HTMLElement;

    wpAdminBarHeight.value = 0;

    if ($wpAdminBar) {
      wpAdminBarHeight.value += $wpAdminBar.offsetHeight;
    }

    header.top = wpAdminBarHeight.value;

    if (header.$el) {
      headerHeight.value = header.$el.offsetHeight + wpAdminBarHeight.value;
    }
  });
};
const minAmountToScroll = ref(0);
const scrollTop = ref(0);
const hideHeader = ref(false);

const setHeaderTop = () => {
  minAmountToScroll.value = 0;
  if (header.$el) {
    minAmountToScroll.value += header.$el.offsetHeight;
  }

  const $wpAdminBar = document.querySelector('#wpadminbar') as HTMLElement;
  if ($wpAdminBar) {
    minAmountToScroll.value += $wpAdminBar.offsetHeight;
  }

  if (window.scrollY > minAmountToScroll.value && window.scrollY > scrollTop.value) {
    hideHeader.value = true;
    store.dispatch('search/close');
    store.dispatch('sidebar/close');
  } else {
    hideHeader.value = false;
  }

  scrollTop.value = window.scrollY;
};

const setHeaderCssVars = () => {
  const $body = document.querySelector('body');
  if (!$body) return;

  let minTransformedHeight = 0;
  const $wpAdminBar = document.querySelector('#wpadminbar') as HTMLElement;
  if ($wpAdminBar) {
    minTransformedHeight += $wpAdminBar.offsetHeight;
  }

  $body.style.setProperty('--app-header-height', `${headerHeight.value}px`);
  $body.style.setProperty('--app-header-height-transformed', `${hideHeader.value ? minTransformedHeight : headerHeight.value}px`);
};

watch(() => `${hideHeader.value}:${headerHeight.value}:${wpAdminBarHeight.value}`, () => {
  nextTick(setHeaderCssVars);
}, {
  deep: true,
  immediate: true,
});

const root = ref<HTMLElement>();
const checkIfMobile = () => {
  if (!root.value) return;
  if (!navBar.value) return;
  if (!logoBar.value) return;

  const cloneToCheck = root.value?.cloneNode(true) as HTMLElement;
  if (!cloneToCheck) return;

  const cloneNavBar = cloneToCheck.querySelector('.navbar-nav') as HTMLElement;
  const cloneLogoBar = cloneToCheck.querySelector('.brand-row') as HTMLElement;
  const cloneNavBarParent = cloneNavBar.parentElement as HTMLElement;

  if (!cloneNavBar) return;
  if (!cloneLogoBar) return;
  if (!cloneNavBarParent) return;

  cloneToCheck.id = 'header-clone';
  cloneToCheck.style.opacity = '0';
  cloneToCheck.style.pointerEvents = 'none';
  cloneToCheck.classList.add('desktop');
  cloneToCheck.classList.remove('mobil');

  cloneNavBar.style.position = 'absolute';
  cloneLogoBar.style.position = 'absolute';

  root.value.parentNode?.insertBefore(cloneToCheck, root.value);

  const parentStyle = window.getComputedStyle(cloneNavBarParent);
  const maxWidth = cloneNavBarParent.offsetWidth
      - parseFloat(parentStyle.paddingLeft)
      - parseFloat(parentStyle.paddingRight);
  const navBarWidth = cloneNavBar.offsetWidth;
  const logoBarWidth = cloneLogoBar.offsetWidth;

  isMobile.value = (navBarWidth + logoBarWidth) > maxWidth;

  cloneToCheck.remove();
};

const route = useRoute();
const currentUrl = computed(() => new URL(route.fullPath, window.location.origin).href);
const menuItemsStore = computed(() => store.getters['menus/menu']('header'));
const menuItems = ref<MenuInterface[]>([]);
const menuItemsTop = ref<MenuInterface[]>([]);
const menuItemsMain = ref<MenuInterface[]>([]);
const menuDepth = ref<number>(0);
const menuDepthCheck = ref<number>(0);

const isMenuItemRecursive = (menuItem: MenuInterface, depth = 1): MenuInterface => {
  const updatedMenuItem = { ...menuItem };

  updatedMenuItem.active = updatedMenuItem.url === currentUrl.value;

  if (Array.isArray(updatedMenuItem.children)) {
    updatedMenuItem.children = updatedMenuItem.children.map((item: MenuInterface) => isMenuItemRecursive(item, depth + 1));

    updatedMenuItem.active = updatedMenuItem.active || !!(updatedMenuItem.children || []).find((m: MenuInterface) => m.active);
  }

  if (depth > menuDepthCheck.value) {
    menuDepthCheck.value = depth;
  }

  return updatedMenuItem;
};

const updateMenuItemActiveByStartOfUrl = (items: MenuInterface[]): MenuInterface[] => {
  const updatedItems = [...items];

  let alreadyHasFoundActive = false;
  updatedItems.forEach((_, index) => {
    if (alreadyHasFoundActive) return;

    updatedItems[index].children = updateMenuItemActiveByStartOfUrl(updatedItems[index].children || []);

    const hasActiveChildren = !!(updatedItems[index].children || []).find((child) => child.active);

    const itemUrl = updatedItems[index].url || '~~~';
    updatedItems[index].active = hasActiveChildren || (
      itemUrl !== `${window.location.origin}/`.trim()
        && currentUrl.value.startsWith(itemUrl)
    );

    alreadyHasFoundActive = !!updatedItems[index].active;
  });

  return updatedItems;
};

const updateMenuItems = () => {
  menuDepthCheck.value = 0;
  let updatedMenuItems = [...(menuItemsStore.value || [])].map(isMenuItemRecursive);
  const hasActiveMenuItem = !!updatedMenuItems.find((item) => item.active);

  if (!hasActiveMenuItem) {
    updatedMenuItems = updateMenuItemActiveByStartOfUrl(updatedMenuItems);
  }

  if (JSON.stringify(updatedMenuItems) === JSON.stringify(menuItems.value)) return;

  menuItems.value = updatedMenuItems;

  if (menuDepth.value !== menuDepthCheck.value) {
    menuDepth.value = menuDepthCheck.value;
  }

  if (menuDepth.value >= 3) {
    const menuItemsTopNew = menuItems.value.map((menuItem) => {
      const menuItemTop = { ...menuItem };
      delete menuItemTop.children;

      return menuItemTop;
    });
    if (JSON.stringify(menuItemsTop.value) !== JSON.stringify(menuItemsTopNew)) {
      menuItemsTop.value = menuItemsTopNew;
    }

    const menuItemsMainNew = (menuItems.value.find((menuItem) => menuItem.active) || menuItems.value[0])?.children || [];
    if (JSON.stringify(menuItemsMain.value) !== JSON.stringify(menuItemsMainNew)) {
      menuItemsMain.value = menuItemsMainNew;
    }
  } else {
    menuItemsTop.value = [];
    if (JSON.stringify(menuItems.value) !== JSON.stringify(menuItemsMain.value)) {
      menuItemsMain.value = menuItems.value;
    }
  }

  nextTick(checkIfMobile);
};

watch(() => ({
  page: currentUrl.value,
  menu: menuItemsStore.value,
}), updateMenuItems, {
  deep: true,
  immediate: true,
});

onBeforeMount(() => {
  store.dispatch('menus/fetchBySlug', 'header');
});

onMounted(() => {
  header.$el = document.querySelector('header');
  window.addEventListener('scroll', setHeaderTop);
  window.addEventListener('resize', checkIfMobile);
  window.addEventListener('load', checkIfMobile);
  window.addEventListener('resize', setHeaderPos);
  window.addEventListener('load', setHeaderPos);
  setHeaderTop();
  checkIfMobile();
  setHeaderPos();
  for (let i = 100; i < 1000; i += 100) {
    setTimeout(setHeaderTop, i);
    setTimeout(setHeaderPos, i);
  }
});

onBeforeUnmount(() => {
  window.addEventListener('scroll', setHeaderTop);
  window.removeEventListener('resize', checkIfMobile);
  window.removeEventListener('load', checkIfMobile);
  window.removeEventListener('resize', setHeaderPos);
  window.removeEventListener('load', setHeaderPos);
});

const cartEnabled = env('CART_ENABLED', false);
const toggleCart = () => store.commit('cart/TOGGLE_SHOW_WIDGET');
const toggleSearch = () => {
  store.dispatch('search/toggle');
};

watch(() => isMobile.value, () => {
  store.dispatch('sidebar/close');
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

html {
  margin-top: 0 !important;
}

:root, body {
  margin: 0;
  padding: 0;
}

body {
  padding-top: var(--app-header-height-transformed);
  transition: padding-top .5s linear;
}

header, #header-clone {
  position: fixed;
  top: 0;
  width: 100%;
  left: 0;
  background-color: #ffffff;
  z-index: 10000;
  transform: translateY(0);
  transition: transform .5s linear;
  padding: 0;

  &.desktop {
    .d-desktop {
      &-block {
        display: block !important;
      }

      &-inline-block {
        display: inline-block !important;
      }
    }
  }

  &.slide-up {
    transform: translateY(-100%);
  }

  .brand-image-link {
    @include font-size(70px);
    padding: 0 0 0 0 !important;
    display: inline-block;
    line-height: 1;
  }

  .brand-company-link {
    @include font-size(48px);
    padding: .25em 0 .25em 1em !important;
    display: inline-block;
    line-height: 1;
  }

  .brand-row {
    width: 100%;
  }

  .brand-company-link {
    display: none;
  }

  .sidebar-toggle {
    line-height: 1;
    outline: 0;
    border: 0;
    background: transparent;
  }

  &.desktop {
    .brand-company-link {
      display: block;
    }

    .sidebar-toggle {
      display: none;
    }

    .brand-image-link {
      padding: .214em .286em .214em 0 !important;
    }

    .brand-row {
      width: auto;
    }
  }

  .brand-image-link {
    img {
      display: inline-block;
      vertical-align: top;
      width: auto;
      height: 1em;
    }

    &.partner {
      margin: 0;
      position: relative;

      img {
        height: .7em;
        margin-top: .15em;
      }

      &:after {
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        display: block;
        height: 1.24em;
        width: 1px;
        background: #DBDBDB;
      }
    }
  }

  > .navbar {
    padding: 0;
    box-shadow: 0px 3px 6px #00000029;

    &-nav {
      width: 100%;
      flex-direction: row;
    }
  }

  .nav-link {
    &__other,
    .sidebar-toggle {
      @include font-size(26px);
      padding: 1.16667em .75em !important;
      color: $body-color;
      line-height: 1;

      @media (min-width: map-get($grid-breakpoints, 'md')) {
        padding: 1.423em 0.769em !important;
      }

      &:last-child {
        padding-right: 0 !important;
      }
    }
  }
}

.mobile-menu {
  right: 1.688rem;
  bottom: 2.625rem;
  position: fixed;
  z-index: 999999;

  svg {
    height: 2.313rem;
  }
}
</style>
